import { GalleryFeedbackOption } from '@picflow/types';
import Stripe from 'stripe';
import { convertPermissions, internalPermissions } from './lib/convert-permissions';

export const getBaseZoom = () => {
  return 1;
};

export const storyblokImageSizes = [
  3000, 2400, 2000, 1800, 1600, 1400, 1200, 1000, 800, 720, 600, 550, 500, 450, 400, 350, 300,
];

export const multiPartUploadPartSize = 1024 * 1024 * 10;

export const multiPartUploadConcurrency = 1;

export const downloadSizes = [
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'small',
    size: 720,
  },
  {
    name: 'medium',
    size: 1920,
  },
  {
    name: 'large',
    size: 3600,
  },
  {
    name: 'original',
  },
];

export const languages: string[] = ['en', 'de', 'fr'];

export const defaultLanguage = 'en';

export const doubleClickTime = 350;

export const biggestGroupTimeDiff = 1000 * 60 * 10;

export const maxAnimatedGifSize = 1024 * 1024 * 3;

export const maxConcurrentUploads = 5;

export const activityPerPage = 50;

export const dragScrollThreshold = 50;

export const assetLoadSettings = {
  galleryScreensToPreloadBefore: 0.5,
  galleryScreensToPreloadAfter: 1,
  galleryScreensToPreGenerate: 5,
  assetPagePreGenerateBefore: 1,
  assetPagePreloadBefore: 1,
  assetPagePreloadAfter: 3,
  assetPagePreGenerateAfter: 10,
  preGenerateHighResFromList: true,
};

export const assetSizes = [320, 480, 640, 720, 1280, 1440, 1920, 2240, 2560, 3840, 4820, 5600, 99999999999999];

export const defaultDataLifetime = 1000 * 60;

export const defaultDarkBackgroundThreshold = 186;

export const trialValueMultiplier = 0.5;

export const thirdPartyScriptDelay = 1000 * 2;

export const tooltipDelay = 300;

export const assetSideMenuSize = 400;

export const maxWatermarkApplyTime = 1000 * 60 * 5;

export const maxUploadRetryCount = 1;
export const maxPostAssetRetryCount = 1;
export const maxUploadFullRetryCount = 1;

// export const maxZipRequestSize = 1; // 1 byte
export const maxZipRequestSize = 1024 * 1024 * 1024 * 1; // 1 GB

export const maxResizeBatchRetryCount = 2;
export const assetsPerResizeBatch = 50;

export const formatsWithTransparency = ['png', 'webp'];
export const browserRenderableExtensions = ['jpg', 'jpeg', 'gif', 'png', 'webp'];
export const allowedTypes = ['jpg', 'jpeg', 'gif', 'png', 'tiff', 'tif', 'webp', 'avif'];
export const allowedVideoTypes = ['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv'];
export const allowedRawTypes = [
  'nef',
  'raf',
  'dng',
  'cr2',
  'cr3',
  'nrw',
  'arw',
  'pef',
  'ptx',
  '3fr',
  'fff',
  'orf',
  'iiq',
  'rw2',
  'raw',
  'rwl',
  'dcr',
  'heic',
  'heif',
];

export const accessLevelOrder = ['no_access', 'view_only', 'review', 'edit'];

export const maxTimeBetweenReloads = 1000 * 60 * 60 * 12;

export const maxFileSize = 1024 * 1024 * 1024 * 5;
export const maxFileSizeNew = 1024 * 1024 * 1024 * 100;

export const trueUpDurationInDays = 14;

export const msInADay = 1000 * 60 * 60 * 24;

export const debugMaxEntries = 1000;

export const tagColors = [null, 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];

export const fontSizeMap = {
  small: 1,
  medium: 2,
  large: 3,
  huge: 4,
};

export const GbForStoragePrice = (price: Stripe.Price) => {
  if (price.lookup_key.includes('_1tb_')) return 1000;
  return 250;
};

export const allFeedbackOptions: GalleryFeedbackOption[] = [
  'favourite',
  'comment',
  'status',
  'color_label',
  'annotation',
];

// This is not great.. need to move this to a backend endpoint

export const productsToSell = ['plus', 'pro', 'team', 'enterprise'];

const scopesFree = [
  'asset:favourites',
  'asset:comments',
  'asset:upload',
  'asset:download',
  'asset:download-original',
  'asset:annotations',
  'asset:status',
];

const scopesBasic = [
  ...scopesFree,
  'core:storage-addons',
  'asset:color-labels',
  'asset-version:multiple',
  'gallery:sub-gallery',
  'gallery:embed-section',
  'gallery:remove-picflow-branding',
  'gallery:force-identification',
  'gallery:password',
];

const scopesPlus = [...scopesBasic, 'tenant:logo-upload', 'gallery:embed-gallery'];

const scopesPro = [
  ...scopesPlus,
  'asset:custom-labels',
  'asset:upload-raw',
  'asset:watermarking',
  'asset:file-attachment',
  'gallery:client-advanced-mode',
  'gallery:approval-limit',
  'gallery:custom-presets',
  'tenant:custom-domain',
  'tenant:custom-fonts',
  'tenant:members',
  'tenant:seat-addons',
];

const scopesTeam = [...scopesPro];
const scopesEnterprise = [
  ...scopesTeam,
  'groups:list',
  'groups:create',
  'groups:retrieve',
  'groups:update',
  'groups:delete',
  'groupUsers:add',
  'groupUsers:remove',
  'groupUsers:list',
  'groupGalleries:attach',
  'groupGalleries:detach',
  'groupGalleries:list',
];

const getBytesFromKilobytes = (kilobytes: number): number => {
  return kilobytes * 1024;
};

const getBytesFromMegabytes = (megabytes: number): number => {
  return getBytesFromKilobytes(megabytes) * 1024;
};

const getBytesFromGigabytes = (gigabytes: number): number => {
  return getBytesFromMegabytes(gigabytes) * 1024;
};

const permissionsFree = convertPermissions(2, scopesFree, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(2),
  storage_addon_limit: 999999999999999,
});

const permissionsBasic = convertPermissions(2, scopesBasic, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(10),
  storage_addon_limit: 999999999999999,
});

const permissionsPlus = convertPermissions(5, scopesPlus, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(100),
  storage_addon_limit: 999999999999999,
});

const permissionsPro = convertPermissions(20, scopesPro, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(1000),
  storage_addon_limit: 999999999999999,
});

const permissionsTeam = convertPermissions(-1, scopesTeam, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(2000),
  storage_addon_limit: 999999999999999,
});
const permissionsEnterprise = convertPermissions(-1, scopesEnterprise, {
  storage_total_limit: 999999999999999,
  storage_plan_limit: getBytesFromGigabytes(10000),
  storage_addon_limit: 999999999999999,
});

export const planPermissions: { [planName: string]: internalPermissions } = {
  'Picflow Free': permissionsFree,
  'Picflow Basic': permissionsBasic,
  'Picflow Plus': permissionsPlus,
  'Picflow Pro': permissionsPro,
  'Picflow Team': permissionsTeam,
  'Picflow Tester': permissionsPro,
  'Picflow Enterprise': permissionsEnterprise,
};

export const extraParamsForCreateGalleryFromPreset = {
  title: '',
  cover: {
    overlay_color: 'rgba(31,35,46,0.2)',
    position: '50% 50%',
    size: 'small',
    uuid: '',
    content_position: <[number, number]>[0.5, 0.5],
  },
};

export const defaultStorageLookupKeys = {
  yearly: 'picflow_storage_addon_1tb_96_usd_yearly',
  monthly: 'picflow_storage_addon_1tb_10_usd_monthly',
};

export const visitorsCacheMaxAge = 1000 * 60 * 5;
export const membersCacheMaxAge = 1000 * 60 * 5;
export const userGroupsMaxAge = 1000 * 60 * 5;
export const galleryMembersMaxAge = 1000 * 60 * 5;

export const getPlanForPermission = (category: string, permission: string): string => {
  const planNames = Object.keys(planPermissions);
  return planNames
    .find(planName => planPermissions[planName].permissions[category][permission])
    .split(' ')[1]
    .toLowerCase();
};
